import { getAverageHourlyPricing } from "$/api/market-monitoring/electricity-analysis.api";
import { Checkbox } from "$/lib/components/atoms";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import CustomTooltip from "$/lib/components/graphs/LineGraph/CustomTooltip";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import { ENERGY_KPIS } from "$/lib/utils/constants/marketMonitoring/graphs.constants";
import {
  format,
  getDate,
  parseDate,
} from "$/lib/utils/functions/date.functions";
import { generateYAxisTicks } from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn, getMinMax } from "$/lib/utils/functions/misc.functions";
import { numberFormatter } from "$/lib/utils/functions/util.functions";
import { useQuery } from "@tanstack/react-query";
import { addDays, differenceInDays } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { customColors } from "tailwind.config";
import { useScreen } from "usehooks-ts";

const AverageHourlyPricingCharts = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const { height: screenHeight } = useScreen();

  const graphHeight = useMemo(
    () => Math.round(screenHeight * 0.7) * (48 / 100),
    [screenHeight],
  );

  const start = new Date();
  start.setUTCDate(start.getUTCDate() - 10);

  const end = new Date();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const [filters, setFilters] = useState<Record<string, boolean>>({
    all: false,
    ...Object.fromEntries(
      Object.keys(ENERGY_KPIS).map((key) => [
        key,
        key === "SOLAR" || key === "BIOMASS" ? true : false,
      ]),
    ),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["get-elec-analysis-hourly-pricing-average", dateRange],
    queryFn: () =>
      getAverageHourlyPricing({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
      }),
  });

  const toDisplayData = useMemo(() => (data ? data.data : []), [data]);

  const keys = useMemo(() => {
    const presetKeys = Object.keys(ENERGY_KPIS);

    const dataKeys = Object.keys(filters).filter(
      (key) => filters[key] && key !== "all",
    );

    const filteredKeys = presetKeys.filter((key) => dataKeys.includes(key));

    return filteredKeys.reverse();
  }, [filters]);

  const displayNames = keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: ENERGY_KPIS[key as keyof typeof ENERGY_KPIS].label,
    }),
    {},
  );

  // const result = getMinMax(toDisplayData, keys);

  const xDomain = useMemo(() => {
    const [start, end] = dateRange;

    const startDate = toDisplayData[0]?.date
      ? parseDate(toDisplayData[0].date.split(" ")[0] ?? "")
      : start;
    const endDate = toDisplayData[toDisplayData.length - 1]?.date
      ? parseDate(
          toDisplayData[toDisplayData.length - 1]?.date.split(" ")[0] ?? "",
        )
      : end;

    const totalDays = differenceInDays(endDate, startDate);
    const shortRange = totalDays <= 6;

    const interval = shortRange ? 1 : Math.ceil(totalDays / 13);

    const ticks: string[] = [];

    for (let i = 0; i < (shortRange ? 6 : 12); i++) {
      const tickDate = addDays(startDate, Math.round(i * interval));
      ticks.push(format(tickDate, "dd/MM/yy") + " - 00 h");
      if (shortRange) ticks.push(format(tickDate, "dd/MM/yy") + " - 12 h");
    }

    ticks.push(
      toDisplayData.length > 0
        ? (toDisplayData.slice(-1)[0]?.date ??
            format(end, "dd/MM/yy") + " - 00 h")
        : format(end, "dd/MM/yy") + " - 00 h",
    );

    return ticks;
  }, [dateRange, toDisplayData]);

  const toDisplauAvgData = useMemo(() => (data ? data.avgData : []), [data]);
  //   const keysAvgData = useMemo(
  //     () => getArrayKeys(toDisplauAvgData),
  //     [toDisplauAvgData],
  //   );
  const resultAvg = useMemo(
    () =>
      getMinMax(toDisplauAvgData, [
        ...keys,
        "Spot Elec moyen journalier (Base)",
      ]),
    [toDisplauAvgData, keys],
  );

  // const [dataAvgDateRange] = useMemo(() => {
  //   // const res1 = generateGraphDateRangeFromData(
  //   //   toDisplayData,
  //   //   "dd/MM/yy HH",
  //   //   "date",
  //   // );

  //   const res2 = generateGraphDateRangeFromData(
  //     toDisplauAvgData,
  //     "dd/MM/yy",
  //     "date",
  //   );

  //   return [res2];
  // }, [toDisplauAvgData]);

  // const [xAvgDomain] = useMemo(() => {
  //   // let res1;
  //   let res2;

  //   // if (dataDateRange && dataDateRange[0] && dataDateRange[1])
  //   //   res1 = generateXAxisTicks(
  //   //     dataDateRange[0],
  //   //     dataDateRange[1],
  //   //     "dd/MM/yy HH",
  //   //   );

  //   if (dataAvgDateRange && dataAvgDateRange[0] && dataAvgDateRange[1])
  //     res2 = generateXAxisTicks(dataAvgDateRange[0], dataAvgDateRange[1]);

  //   return [res2];
  // }, [dataAvgDateRange]);

  const [graphVisibility, setGraphVisibility] = useState<{
    area: GraphFilterItemsProps[];
    line: GraphFilterItemsProps[];
  }>({ area: [], line: [] });

  useEffect(() => {
    setGraphVisibility({
      line: [
        {
          name: "Spot Elec Horaire",
          color: "#000000",
        },
        {
          name: "Spot Elec moyen journalier (Base)",
          color: "#535353",
        },
      ],
      area: Object.entries(filters)
        .filter(([key, value]) => value && key !== "all")
        .map(([key]) => ({
          name: ENERGY_KPIS[key as keyof typeof ENERGY_KPIS].label,
          color: ENERGY_KPIS[key as keyof typeof ENERGY_KPIS].color,
        })),
    });
  }, [filters]);

  return (
    <DisplayBox
      title="Prix de capture horaire et journalier par filière "
      Icon={LineChartIcon}
      theme="blue"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth className="gap-4">
            <ResponsiveContainer width="100%" height={graphHeight}>
              <ComposedChart
                data={toDisplayData}
                margin={{ top: 10, right: 0, left: 0, bottom: 10 }}
              >
                <XAxis
                  dataKey="date"
                  // interval="preserveStartEnd"
                  interval={0}
                  ticks={xDomain}
                  tick={(props) => {
                    return (
                      <g>
                        <text
                          {...props}
                          x={props.x}
                          y={props.y + 10}
                          textAnchor="middle"
                          fontSize={11}
                        >
                          {props.payload.value.split(" - ")[0]}
                        </text>
                        <text
                          {...props}
                          x={props.x}
                          y={props.y + 25}
                          textAnchor="middle"
                          fontSize={10}
                        >
                          {props.payload.value.split(" - ")[1]}
                        </text>
                      </g>
                    );
                  }}
                  fontSize={12}
                  tickMargin={5}
                />

                <YAxis
                  yAxisId={0}
                  tickSize={2}
                  width={85}
                  fontWeight={500}
                  fontSize={12}
                  tickMargin={5}
                  unit=" MWh"
                  orientation="left"
                  interval={0}
                  tickFormatter={numberFormatter}
                  // ticks={generateYAxisTicks(
                  //   result.min.value,
                  //   result.max.value,
                  //   10,
                  // )}
                  // domain={[result.min.value, result.max.value]}
                />

                <YAxis
                  yAxisId={1}
                  tickSize={2}
                  width={85}
                  fontWeight={500}
                  fontSize={12}
                  tickMargin={5}
                  unit=" €/MWh"
                  tickFormatter={numberFormatter}
                  orientation="right"
                />

                <Tooltip
                  content={
                    <CustomTooltip
                      unit=" MWh"
                      displayUnits={{
                        "Spot Elec Horaire": " €/MWh",
                      }}
                      reverseTooltipOrder
                      displayNames={displayNames}
                    />
                  }
                  wrapperStyle={{ zIndex: 1000 }}
                />

                {keys.map((key) => (
                  <Area
                    key={key}
                    yAxisId={0}
                    type="monotone"
                    dataKey={key}
                    stackId="1"
                    stroke={ENERGY_KPIS[key as keyof typeof ENERGY_KPIS].color}
                    fill={ENERGY_KPIS[key as keyof typeof ENERGY_KPIS].color}
                    fillOpacity={1}
                    tooltipType="none"
                  />
                ))}

                <Line
                  type="monotone"
                  yAxisId={1}
                  dataKey="Spot Elec Horaire"
                  stroke="black"
                  dot={false}
                  activeDot={false}
                  strokeWidth={2}
                  connectNulls
                />

                <ReferenceLine
                  y={0}
                  yAxisId={1}
                  stroke={customColors.tertiary.DEFAULT}
                  strokeDasharray={5}
                />
              </ComposedChart>
            </ResponsiveContainer>

            <ResponsiveContainer width="100%" height={graphHeight}>
              <ComposedChart
                data={toDisplauAvgData}
                margin={{ top: 10, right: 84, left: 0, bottom: 10 }}
              >
                <XAxis
                  dataKey="date"
                  fontSize={12}
                  tickMargin={5}
                  interval={0}
                  tick={(props) => {
                    return (
                      <g>
                        <text
                          {...props}
                          x={props.x}
                          y={props.y + 10}
                          textAnchor="middle"
                          fontSize={11}
                        >
                          {props.payload.value.split(" - ")[0]}
                        </text>
                        <text
                          {...props}
                          x={props.x}
                          y={props.y + 25}
                          textAnchor="middle"
                          fontSize={10}
                        >
                          {props.payload.value.split(" - ")[1]}
                        </text>
                      </g>
                    );
                  }}
                  ticks={xDomain}
                />

                <YAxis
                  tickSize={2}
                  width={85}
                  fontWeight={500}
                  fontSize={12}
                  tickMargin={5}
                  unit=" €/MWh"
                  orientation="left"
                  tickFormatter={numberFormatter}
                  interval={0}
                  ticks={generateYAxisTicks(
                    resultAvg.min.value,
                    resultAvg.max.value,
                    10,
                  )}
                  domain={[resultAvg.min.value, resultAvg.max.value]}
                />

                <Tooltip
                  content={
                    <CustomTooltip
                      unit=" €/MWh"
                      displayNames={displayNames}
                      reverseTooltipOrder
                    />
                  }
                />

                {keys.map((key) => (
                  <Line
                    key={key}
                    type="stepAfter"
                    dataKey={key}
                    stroke={ENERGY_KPIS[key as keyof typeof ENERGY_KPIS].color}
                    fill={ENERGY_KPIS[key as keyof typeof ENERGY_KPIS].color}
                    fillOpacity={1}
                    tooltipType="none"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                ))}

                <Line
                  type="stepAfter"
                  dataKey="Spot Elec moyen journalier (Base)"
                  stroke="black"
                  dot={false}
                  strokeWidth={2}
                  connectNulls
                />

                <ReferenceLine
                  y={0}
                  stroke={customColors.tertiary.DEFAULT}
                  strokeDasharray={5}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
          />

          <Flexbox className="min-w-full gap-3.5 overflow-y-auto pb-1">
            <p className="whitespace-nowrap text-base font-semibold text-secondary">
              Filiére
            </p>
            <label
              htmlFor="elec-analysis-hourly-pricing-energy-kpis"
              className="flex items-center gap-2"
            >
              <Checkbox
                checked={filters.all}
                onChange={() => {
                  if (filters.all) {
                    setFilters({
                      all: false,
                      ...Object.fromEntries(
                        Object.keys(ENERGY_KPIS).map((key) => [key, false]),
                      ),
                    });
                  } else {
                    setFilters({
                      all: true,
                      ...Object.fromEntries(
                        Object.keys(ENERGY_KPIS).map((key) => [key, true]),
                      ),
                    });
                  }
                }}
              />
              <span className="text-sm font-medium text-primary">(Tout)</span>
            </label>

            {Object.keys(ENERGY_KPIS).map((key) => (
              <label
                key={key}
                htmlFor="elec-analysis-hourly-pricing-energy-kpis"
                className="flex items-center gap-2"
              >
                <Checkbox
                  checked={filters[key]}
                  onChange={() =>
                    setFilters((prev) => ({ ...prev, [key]: !prev[key] }))
                  }
                />
                <span className="text-sm font-medium text-primary">
                  {ENERGY_KPIS[key as keyof typeof ENERGY_KPIS].label}
                </span>
              </label>
            ))}

            <GraphFilter
              title="Filiére"
              data={graphVisibility.area}
              handleOnChange={() => {}}
            />
            <GraphFilter
              title="Légende"
              data={graphVisibility.line}
              handleOnChange={() => {}}
            />
          </Flexbox>
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default AverageHourlyPricingCharts;
